// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {Fragment, useContext, useRef, useState, useEffect} from 'react';
import {Button, t_button_color} from '../../../hola/pub/lib/button';
import styles from './hp_cards.module.less';
import {use_section_scroll} from '../../../hola/pub/lib/use_section_scroll';
import cn from 'classnames';
import {Settings_context} from '../../../hola/pub/settings_ctx';
import {use_translation_v} from '../../../hola/pub/lib/trans_v';
import Icon_arrow_right from '../../../hola/pub/img/hola2023/icons/24/arrow_right.svg';
import {useLocation} from 'react-router-dom';
import {use_qs} from '../../../hola/pub/lib/use_qs';
import {get_new_link} from '../../../hola/pub/lib/use_pathname';

type t_color = 'brown' | 'pink' | 'green' | 'purple' | 'blue' | 'light';
type t_version = 'v1' | 'v2';

interface i_text_item {
    value: JSX.Element;
}

interface i_hp_card {
    img?: JSX.Element;
    title: string|JSX.Element;
    text: string | i_text_item[];
    color?: t_color;
    cta?: {
        caption: JSX.Element;
        to: string;
        color?: t_button_color;
        on_click?: (ev:any)=>void;
    };
    reverse?: boolean;
    cls?: string;
    show?: boolean;
    version?: t_version;
}

interface i_hp_cards_props {
    version?: t_version;
}

const pricing_paths = {
    'residential-proxy': get_new_link('/pricing/residential-proxies'),
    'dedicated-datacenter':
        get_new_link('/pricing/dedicated-data-center-proxies'),
    'shared-datacenter': get_new_link('/pricing/shared-data-center-proxies'),
    'isp-proxy': get_new_link('/pricing/isp-proxies')
};

const use_pricing_path = ()=>{
    const location = useLocation();
    const query_params = use_qs();
    const get_product_type = ()=>{
        const path = location.pathname;
        return path.includes('/products/') ?
            path.split('/products/')[1]?.split('/')[0] : '';
    };
    const get_path = ()=>{
        const type = get_product_type();
        if (type == 'isp-proxy' && query_params.type == 'isp_plus')
            return get_new_link('/pricing/search-engine-isp-proxies');
        return get_new_link(pricing_paths[type] || '/pricing');
    };
    const [path, set_path] = useState(get_path());
    useEffect(()=>{
        set_path(get_path());
    }, [location.pathname, query_params.type]);
    return path;
};

export const HP_card = (props: i_hp_card)=>{
    const {img, title, text, cta, color = 'purple', reverse, cls,
        show, version = 'v1'} = props;
    const ref = useRef();
    const card_cls = cn(styles.hp_card, cls, styles[color], reverse &&
        styles.reverse,
    );
    const {T} = use_translation_v({no_defaults: true});
    const {theme} = useContext(Settings_context);
    use_section_scroll(ref, show, {treshold: 0.8});
    const scroll_to_top = e=>{
        e.preventDefault();
        window.scrollTo(0, 0);
    };
    const render_text = ()=>{
        if (version == 'v1')
            return <p><T>{text as string}</T></p>;
        return <ul className={styles.bullet_list}>
          {(text as i_text_item[]).map((item, i)=>
            <li key={i} className={cn(theme.p16, theme.reg)}>
              {item.value}
            </li>
          )}
        </ul>;
    };
    return <div className={card_cls} ref={ref}>
      <div className={styles.text}>
        {typeof title=='string' ? <h4><T>{title}</T></h4> : <T>{title}</T>}
        {render_text()}
        {cta && <Button color={cta.color || 'black'} to={cta.to}
          caption={cta.caption} round={false} fit
          on_click={location.pathname == cta.to ?
            scroll_to_top : cta.on_click}/>}
      </div>
      {img && <div className={styles.img}>{img}</div>}
    </div>;
};

const hp_cards: (i_hp_card & {id: string})[] = [
    {
        id: 'scale',
        title: 'Scale your business',
        text: `Proxies are the secret sauce to scaling your business. By
            routing activities through different IPs, proxies let you gather
            data, manage accounts, and automate tasks. This expands
            reach, analyzes competitors, and makes decisions efficient. Proxies
            empower your business to grow smarter and faster!`,
        img: <div className={cn(styles.card_img, styles.scale)}/>,
        cta: {
            caption: <Fragment>Get started <Icon_arrow_right/></Fragment>,
            to: '/dashboard',
            color: 'transparent_black',
        },
    },
    {
        id: 'overcome',
        title: 'Overcome restrictions',
        text: `Proxies help bypass online restrictions by masking your IP
            address, allowing access to content and data from anywhere. They
            keep your activities hidden, ensuring you navigate blocks and
            firewalls smoothly, making it easier to reach your goals
            without barriers.`,
        img: <div className={cn(styles.card_img, styles.overcome)}/>,
        cta: {
            caption: <Fragment>See all our locations <Icon_arrow_right/>
                </Fragment>,
            to: get_new_link('/proxy-location'),
            color: 'transparent_black',
        },
        color: 'green',
        reverse: true,
    },
    {
        id: 'prices',
        title: 'Get the best prices',
        text: `We offer tailored proxy solutions that fit your specific
            needs. With competitive pricing and flexible plans, toolip makes
            sure you only pay for what you need, helping your pocket while
            still getting top-notch performance.`,
        img: <div className={cn(styles.card_img, styles.prices)}/>,
        cta: {
            caption: <Fragment>See our pricing <Icon_arrow_right/></Fragment>,
            to: '',
            color: 'transparent_black',
        },
        color: 'pink',
    },
];

const hp_cards_v2: (i_hp_card & {id: string})[] = [
    {
        id: 'speed',
        title: 'Advanced Proxy Features',
        text: [
            {
                value: <><b>Unmatched Performance:</b> High-speed, low-latency
                  connections.</>,
            },
            {
                value: <><b>Seamless Integrations:</b> API access for easy use
                  in your applications.</>,
            }
        ],
        img: <div className={cn(styles.card_img, styles.speed)}/>,
        version: 'v2',
        color: 'purple',
        cta: {
            caption: <Fragment>Get started <Icon_arrow_right/></Fragment>,
            to: '/dashboard',
            color: 'transparent_black',
        }
    },
    {
        id: 'coverage',
        title: 'Global Coverage',
        text: [
            {
                value: <>Millions of IPs in <b>over 150 countries</b>.</>,
            },
            {
                value: <>Reliable proxies with high uptime and stability.</>,
            }
        ],
        img: <div className={cn(styles.card_img, styles.coverage)}/>,
        version: 'v2',
        color: 'green',
        reverse: true,
        cta: {
            caption: <Fragment>See all our locations <Icon_arrow_right/>
                </Fragment>,
            to: get_new_link('/proxy-location'),
            color: 'transparent_black',
        }
    },
    {
        id: 'tailored',
        title: 'Tailored to Your Needs',
        text: [
            {
                value: <>Rotate or stick with a single IP - your choice!</>,
            },
            {
                value: <>Flexible pay-as-you-go plans for projects of all
                  sizes.</>,
            }
        ],
        img: <div className={cn(styles.card_img, styles.tailored)}/>,
        version: 'v2',
        color: 'pink',
        cta: {
            caption: <Fragment>See our pricing <Icon_arrow_right/></Fragment>,
            to: '',
            color: 'transparent_black',
        }
    }
];

export const HP_cards = (props: i_hp_cards_props)=>{
    const {theme} = useContext(Settings_context);
    const {version = 'v1'} = props;
    const pricing_path = use_pricing_path();
    const cards = version == 'v1' ? [
        hp_cards[0],
        hp_cards[1],
        {...hp_cards[2], cta: {...hp_cards[2].cta, to: pricing_path}}
    ] : [
        hp_cards_v2[0],
        hp_cards_v2[1],
        {...hp_cards_v2[2], cta: {...hp_cards_v2[2].cta, to: pricing_path}}
    ];
    return <section className={styles.hp_cards}>
      <div className={cn(theme.container, styles.container)}>
        {cards.map((c, i)=>{
          return <HP_card key={c.id} {...c}
            show={i==0} version={version}/>;
        })}
      </div>
    </section>;
};
